import React, { useState } from "react";
import {
    Badge,
    Box,
    Card,
    CardBody,
    Divider,
    Flex,
    Grid,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    VStack,
    Button,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Code,
} from "@chakra-ui/react";
import {
    FiCopy,
    FiEye,
    FiMail,
    FiMoreHorizontal,
    FiSend,
    FiStar,
    FiTrash2,
    FiCode,
    FiMonitor
} from "react-icons/fi";
import { formatDateAndTime } from "../../utils/formatDateTime";
import { getTemplateTypeLabel, getStatusColor } from "./constants/emailConstants";

const EmailPreviewModal = ({ isOpen, onClose, template }) => {
    const modalBg = useColorModeValue("white", "gray.800");
    const codeBg = useColorModeValue("gray.50", "gray.700");

    if (!template) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay />
            <ModalContent bg={modalBg} maxH="90vh">
                <ModalHeader>
                    <VStack align="start" spacing={1}>
                        <Text>{template.name}</Text>
                        <Text fontSize="sm" color="gray.500">
                            {template.subject || "No subject"}
                        </Text>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Tabs>
                        <TabList>
                            <Tab>
                                <Icon as={FiMonitor} mr={2} />
                                Preview
                            </Tab>
                            <Tab>
                                <Icon as={FiCode} mr={2} />
                                HTML Source
                            </Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel p={0} pt={4}>
                                <Box
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    minH="400px"
                                    bg="white"
                                >
                                    <iframe
                                        srcDoc={template.htmlContent || '<p>No content available</p>'}
                                        style={{
                                            width: '100%',
                                            height: '500px',
                                            border: 'none',
                                            borderRadius: '6px'
                                        }}
                                        title={`Preview of ${template.name}`}
                                    />
                                </Box>
                            </TabPanel>
                            <TabPanel p={0} pt={4}>
                                <Box
                                    bg={codeBg}
                                    borderRadius="md"
                                    p={4}
                                    maxH="500px"
                                    overflowY="auto"
                                >
                                    <Code
                                        display="block"
                                        whiteSpace="pre-wrap"
                                        fontSize="sm"
                                        bg="transparent"
                                        p={0}
                                    >
                                        {template.htmlContent || 'No HTML content available'}
                                    </Code>
                                </Box>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Close
                    </Button>
                    <Button colorScheme="blue" onClick={() => {
                        // You can add edit functionality here
                        onClose();
                    }}>
                        Edit Template
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const TemplateTable = ({
    templates,
    loading,
    templateTypes = [], // Add templateTypes prop
    onEdit,
    onTest,
    onDuplicate,
    onSetDefault,
    onDelete,
}) => {
    const cardBg = useColorModeValue("white", "gray.700");
    const headerTextColor = useColorModeValue("gray.700", "gray.200");
    const labelTextColor = useColorModeValue("gray.500", "gray.400");
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const handlePreview = (template) => {
        setSelectedTemplate(template);
        onOpen();
    };

    if (loading) {
        return (
            <Card bg={cardBg}>
                <CardBody>
                    <Flex justify="center" align="center" h="400px">
                        <Spinner size="xl" />
                    </Flex>
                </CardBody>
            </Card>
        );
    }

    return (
        <>
            {/* Desktop Table View */}
            <Card bg={cardBg} display={{ base: "none", lg: "block" }}>
                <CardBody p={0}>
                    <Box overflowX="auto" borderRadius="md">
                        {/* Table Header */}
                        <Box
                            bg="gray.50"
                            display="grid"
                            gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr"
                            borderRadius="md"
                            mb={2}
                            minW="1000px"
                            borderBottom="1px solid"
                            borderColor="gray.200"
                        >
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Template Name
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Type
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Status
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Default
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Usage Count
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Last Used
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Created
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                                textAlign="center"
                            >
                                Actions
                            </Box>
                        </Box>
                        {/* Table Body */}
                        <Box minW="1000px">
                            {templates.map((template) => (
                                <Box
                                    key={template.id}
                                    display="grid"
                                    gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr"
                                    alignItems="center"
                                    py={3}
                                    px={4}
                                    mb={2}
                                    borderRadius="md"
                                    _hover={{
                                        bg: "gray.50",
                                        transition: "all 0.2s ease"
                                    }}
                                >
                                    {/* Template Name Column */}
                                    <Box display="flex" alignItems="center">
                                        <VStack align="start" spacing={1}>
                                            <Flex align="center" gap={2}>
                                                <Text fontWeight="semibold">
                                                    {template.name}
                                                </Text>
                                                {template.htmlContent && (
                                                    <IconButton
                                                        icon={<FiEye />}
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="blue"
                                                        onClick={() => handlePreview(template)}
                                                        aria-label="Preview template"
                                                        title="Preview HTML content"
                                                    />
                                                )}
                                            </Flex>
                                            {template.description && (
                                                <Text fontSize="sm" color="gray.500" noOfLines={1} maxW="250px">
                                                    {template.description}
                                                </Text>
                                            )}
                                            {template.subject && (
                                                <Text fontSize="xs" color="gray.400" noOfLines={1} maxW="250px">
                                                    Subject: {template.subject}
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>
                                    {/* Type Column */}
                                    <Box display="flex" alignItems="center">
                                        <Badge colorScheme="blue" variant="subtle" maxW="150px" noOfLines={1}>
                                            {getTemplateTypeLabel(template.type, templateTypes)}
                                        </Badge>
                                    </Box>

                                    {/* Status Column */}
                                    <Box display="flex" alignItems="center">
                                        <Badge
                                            colorScheme={getStatusColor(template.status)}
                                            variant="subtle"
                                            maxW="120px"
                                            noOfLines={1}
                                        >
                                            {template.status.charAt(0).toUpperCase() +
                                                template.status.slice(1)}
                                        </Badge>
                                    </Box>

                                    {/* Default Column */}
                                    <Box display="flex" alignItems="center">
                                        {template.isDefault ? (
                                            <Badge colorScheme="green" variant="solid">
                                                <Icon as={FiStar} mr={1} />
                                                Default
                                            </Badge>
                                        ) : (
                                            <Text color="gray.500">-</Text>
                                        )}
                                    </Box>

                                    {/* Usage Count Column */}
                                    <Box display="flex" alignItems="center">
                                        <Text>{template.usageCount || 0}</Text>
                                    </Box>

                                    {/* Last Used Column */}
                                    <Box display="flex" alignItems="center">
                                        <Text fontSize="sm">
                                            {template.lastUsed
                                                ? formatDateAndTime(template.lastUsed).formattedDate
                                                : 'Never'}
                                        </Text>
                                    </Box>

                                    {/* Created Column */}
                                    <Box display="flex" alignItems="center">
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="sm">
                                                {formatDateAndTime(template.createdAt).formattedDate}
                                            </Text>
                                            {template.createdBy && (
                                                <Text fontSize="xs" color="gray.500">
                                                    by {template.createdBy}
                                                </Text>
                                            )}
                                            {template.updatedAt && template.updatedAt !== template.createdAt && (
                                                <Text fontSize="xs" color="gray.400">
                                                    Updated: {formatDateAndTime(template.updatedAt).formattedDate}
                                                    {template.updatedBy && ` by ${template.updatedBy}`}
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>

                                    {/* Actions Column */}
                                    <Box display="flex" alignItems="center" justifyContent="center">
                                        <Menu>
                                            <MenuButton
                                                as={IconButton}
                                                icon={<FiMoreHorizontal />}
                                                variant="ghost"
                                                size="sm"
                                            />
                                            <MenuList>
                                                {template.htmlContent && (
                                                    <>
                                                        <MenuItem
                                                            icon={<FiEye />}
                                                            onClick={() => handlePreview(template)}
                                                        >
                                                            Preview HTML
                                                        </MenuItem>
                                                        <Divider />
                                                    </>
                                                )}
                                                <MenuItem
                                                    icon={<FiEye />}
                                                    onClick={() => onEdit(template)}
                                                >
                                                    View/Edit
                                                </MenuItem>
                                                <MenuItem
                                                    icon={<FiSend />}
                                                    onClick={() => onTest(template)}
                                                >
                                                    Send Test
                                                </MenuItem>
                                                <MenuItem
                                                    icon={<FiCopy />}
                                                    onClick={() => onDuplicate(template)}
                                                >
                                                    Duplicate
                                                </MenuItem>
                                                {!template.isDefault && (
                                                    <MenuItem
                                                        icon={<FiStar />}
                                                        onClick={() => onSetDefault(template)}
                                                    >
                                                        Set as Default
                                                    </MenuItem>
                                                )}
                                                <Divider />
                                                <MenuItem
                                                    icon={<FiTrash2 />}
                                                    color="red.500"
                                                    onClick={() => onDelete(template)}
                                                    isDisabled={template.isDefault}
                                                >
                                                    Delete
                                                </MenuItem>
                                            </MenuList>
                                        </Menu>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {templates.length === 0 && (
                            <Flex justify="center" align="center" h="300px" direction="column" p={8}>
                                <Icon as={FiMail} w={16} h={16} color="gray.400" mb={4} />
                                <Text color="gray.500" fontSize="lg" fontWeight="semibold" mb={2}>
                                    No email templates found
                                </Text>
                                <Text color="gray.400" fontSize="sm" textAlign="center" maxW="300px">
                                    Create your first template to get started with email automation
                                </Text>
                            </Flex>
                        )}
                    </Box>
                </CardBody>
            </Card>

            {/* Tablet View */}
            <Card bg={cardBg} display={{ base: "none", md: "block", lg: "none" }}>
                <CardBody p={0}>
                    <Box overflowX="auto" borderRadius="md">
                        {/* Tablet Table Header */}
                        <Box
                            bg="gray.50"
                            display="grid"
                            gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 1fr"
                            borderRadius="md"
                            mb={2}
                            minW="800px"
                            borderBottom="1px solid"
                            borderColor="gray.200"
                        >
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Template Name
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Type
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Status
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Default
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                            >
                                Usage
                            </Box>
                            <Box
                                fontWeight="bold"
                                textTransform="uppercase"
                                py={3}
                                px={4}
                                color={headerTextColor}
                                fontSize="xs"
                                letterSpacing="wider"
                                textAlign="center"
                            >
                                Actions
                            </Box>
                        </Box>
                        {/* Tablet Table Body */}
                        <Box minW="800px">
                            {templates.map((template) => (
                                <Box
                                    key={template.id}
                                    display="grid"
                                    gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 1fr"
                                    alignItems="center"
                                    py={3}
                                    px={4}
                                    mb={2}
                                    borderRadius="md"
                                    _hover={{
                                        bg: "gray.50",
                                        transition: "all 0.2s ease"
                                    }}
                                >
                                    {/* Template Name Column */}
                                    <Box display="flex" alignItems="center">
                                        <VStack align="start" spacing={1}>
                                            <Flex align="center" gap={2}>
                                                <Text fontWeight="semibold" fontSize="sm">
                                                    {template.name}
                                                </Text>
                                                {template.htmlContent && (
                                                    <IconButton
                                                        icon={<FiEye />}
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="blue"
                                                        onClick={() => handlePreview(template)}
                                                        aria-label="Preview template"
                                                    />
                                                )}
                                            </Flex>
                                            {template.description && (
                                                <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="200px">
                                                    {template.description}
                                                </Text>
                                            )}
                                            {template.subject && (
                                                <Text fontSize="xs" color="gray.400" noOfLines={1} maxW="200px">
                                                    Subject: {template.subject}
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>
                                    {/* Type Column */}
                                    <Box display="flex" alignItems="center">
                                        <Badge colorScheme="blue" variant="subtle" fontSize="xs" maxW="120px" noOfLines={1}>
                                            {getTemplateTypeLabel(template.type, templateTypes)}
                                        </Badge>
                                    </Box>
                                    {/* Status Column */}
                                    <Box display="flex" alignItems="center">
                                        <Badge
                                            colorScheme={getStatusColor(template.status)}
                                            variant="subtle"
                                            fontSize="xs"
                                            maxW="100px"
                                            noOfLines={1}
                                        >
                                            {template.status.charAt(0).toUpperCase() +
                                                template.status.slice(1)}
                                        </Badge>
                                    </Box>
                                    {/* Default Column */}
                                    <Box display="flex" alignItems="center">
                                        {template.isDefault ? (
                                            <Badge colorScheme="green" variant="solid" fontSize="xs">
                                                <Icon as={FiStar} mr={1} />
                                                Default
                                            </Badge>
                                        ) : (
                                            <Text color="gray.500" fontSize="xs">-</Text>
                                        )}
                                    </Box>
                                    {/* Usage Count Column */}
                                    <Box display="flex" alignItems="center">
                                        <Text fontSize="sm">{template.usageCount || 0}</Text>
                                    </Box>
                                    {/* Actions Column */}
                                    <Box display="flex" alignItems="center" justifyContent="center">
                                        <Menu>
                                            <MenuButton
                                                as={IconButton}
                                                icon={<FiMoreHorizontal />}
                                                variant="ghost"
                                                size="sm"
                                            />
                                            <MenuList>
                                                {template.htmlContent && (
                                                    <>
                                                        <MenuItem
                                                            icon={<FiEye />}
                                                            onClick={() => handlePreview(template)}
                                                        >
                                                            Preview HTML
                                                        </MenuItem>
                                                        <Divider />
                                                    </>
                                                )}
                                                <MenuItem
                                                    icon={<FiEye />}
                                                    onClick={() => onEdit(template)}
                                                >
                                                    View/Edit
                                                </MenuItem>
                                                <MenuItem
                                                    icon={<FiSend />}
                                                    onClick={() => onTest(template)}
                                                >
                                                    Send Test
                                                </MenuItem>
                                                <MenuItem
                                                    icon={<FiCopy />}
                                                    onClick={() => onDuplicate(template)}
                                                >
                                                    Duplicate
                                                </MenuItem>
                                                {!template.isDefault && (
                                                    <MenuItem
                                                        icon={<FiStar />}
                                                        onClick={() => onSetDefault(template)}
                                                    >
                                                        Set as Default
                                                    </MenuItem>
                                                )}
                                                <Divider />
                                                <MenuItem
                                                    icon={<FiTrash2 />}
                                                    color="red.500"
                                                    onClick={() => onDelete(template)}
                                                    isDisabled={template.isDefault}
                                                >
                                                    Delete
                                                </MenuItem>
                                            </MenuList>
                                        </Menu>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {templates.length === 0 && (
                            <Flex justify="center" align="center" h="300px" direction="column" p={8}>
                                <Icon as={FiMail} w={16} h={16} color="gray.400" mb={4} />
                                <Text color="gray.500" fontSize="lg" fontWeight="semibold" mb={2}>
                                    No email templates found
                                </Text>
                                <Text color="gray.400" fontSize="sm" textAlign="center" maxW="300px">
                                    Create your first template to get started with email automation
                                </Text>
                            </Flex>
                        )}
                    </Box>
                </CardBody>
            </Card>

            {/* Mobile Card View */}
            <Box display={{ base: "block", md: "none" }}>
                {templates.map((template) => (
                    <Card key={template.id} bg={cardBg} mb={4} borderRadius="md" shadow="sm">
                        <CardBody p={4}>
                            <VStack spacing={4} align="stretch">
                                {/* Template Header */}
                                <Flex justify="space-between" align="start">
                                    <VStack align="start" spacing={1} flex="1">
                                        <Flex align="center" gap={2}>
                                            <Text fontWeight="semibold" fontSize="lg">
                                                {template.name}
                                            </Text>
                                            {template.htmlContent && (
                                                <IconButton
                                                    icon={<FiEye />}
                                                    size="xs"
                                                    variant="ghost"
                                                    colorScheme="blue"
                                                    onClick={() => handlePreview(template)}
                                                    aria-label="Preview template"
                                                />
                                            )}
                                        </Flex>
                                        {template.description && (
                                            <Text fontSize="sm" color="gray.500" noOfLines={2} maxW="100%">
                                                {template.description}
                                            </Text>
                                        )}
                                        {template.subject && (
                                            <Text fontSize="xs" color="gray.400" noOfLines={1} maxW="100%">
                                                Subject: {template.subject}
                                            </Text>
                                        )}
                                    </VStack>
                                    <Menu>
                                        <MenuButton
                                            as={IconButton}
                                            icon={<FiMoreHorizontal />}
                                            variant="ghost"
                                            size="sm"
                                        />
                                        <MenuList>
                                            {template.htmlContent && (
                                                <>
                                                    <MenuItem
                                                        icon={<FiEye />}
                                                        onClick={() => handlePreview(template)}
                                                    >
                                                        Preview HTML
                                                    </MenuItem>
                                                    <Divider />
                                                </>
                                            )}
                                            <MenuItem
                                                icon={<FiEye />}
                                                onClick={() => onEdit(template)}
                                            >
                                                View/Edit
                                            </MenuItem>
                                            <MenuItem
                                                icon={<FiSend />}
                                                onClick={() => onTest(template)}
                                            >
                                                Send Test
                                            </MenuItem>
                                            <MenuItem
                                                icon={<FiCopy />}
                                                onClick={() => onDuplicate(template)}
                                            >
                                                Duplicate
                                            </MenuItem>
                                            {!template.isDefault && (
                                                <MenuItem
                                                    icon={<FiStar />}
                                                    onClick={() => onSetDefault(template)}
                                                >
                                                    Set as Default
                                                </MenuItem>
                                            )}
                                            <Divider />
                                            <MenuItem
                                                icon={<FiTrash2 />}
                                                color="red.500"
                                                onClick={() => onDelete(template)}
                                                isDisabled={template.isDefault}
                                            >
                                                Delete
                                            </MenuItem>
                                        </MenuList>
                                    </Menu>
                                </Flex>

                                {/* Template Details Grid */}
                                <Box>
                                    <Grid 
                                        templateColumns={{ 
                                            base: "repeat(1, 1fr)", 
                                            sm: "repeat(2, 1fr)" 
                                        }} 
                                        gap={4}
                                    >
                                        {/* Type */}
                                        <Box>
                                            <Text fontSize="xs" color={labelTextColor} mb={1} fontWeight="semibold">TYPE</Text>
                                            <Badge colorScheme="blue" variant="subtle" fontSize="sm" maxW="100%" noOfLines={1}>
                                                {getTemplateTypeLabel(template.type, templateTypes)}
                                            </Badge>
                                        </Box>

                                        {/* Status */}
                                        <Box>
                                            <Text fontSize="xs" color={labelTextColor} mb={1} fontWeight="semibold">STATUS</Text>
                                            <Badge
                                                colorScheme={getStatusColor(template.status)}
                                                variant="subtle"
                                                fontSize="sm"
                                                maxW="100%"
                                                noOfLines={1}
                                            >
                                                {template.status.charAt(0).toUpperCase() +
                                                    template.status.slice(1)}
                                            </Badge>
                                        </Box>

                                        {/* Default */}
                                        <Box>
                                            <Text fontSize="xs" color={labelTextColor} mb={1} fontWeight="semibold">DEFAULT</Text>
                                            {template.isDefault ? (
                                                <Badge colorScheme="green" variant="solid" fontSize="sm" maxW="100%" noOfLines={1}>
                                                    <Icon as={FiStar} mr={1} />
                                                    Default
                                                </Badge>
                                            ) : (
                                                <Text color={labelTextColor} fontSize="sm">-</Text>
                                            )}
                                        </Box>

                                        {/* Usage Count */}
                                        <Box>
                                            <Text fontSize="xs" color={labelTextColor} mb={1} fontWeight="semibold">USAGE COUNT</Text>
                                            <Text fontSize="sm" fontWeight="medium">{template.usageCount || 0}</Text>
                                        </Box>

                                        {/* Last Used */}
                                        <Box>
                                            <Text fontSize="xs" color={labelTextColor} mb={1} fontWeight="semibold">LAST USED</Text>
                                            <Text fontSize="sm">
                                                {template.lastUsed
                                                    ? formatDateAndTime(template.lastUsed).formattedDate
                                                    : 'Never'}
                                            </Text>
                                        </Box>

                                        {/* Created */}
                                        <Box>
                                            <Text fontSize="xs" color={labelTextColor} mb={1} fontWeight="semibold">CREATED</Text>
                                            <VStack align="start" spacing={0}>
                                                <Text fontSize="sm" fontWeight="medium">
                                                    {formatDateAndTime(template.createdAt).formattedDate}
                                                </Text>
                                                {template.createdBy && (
                                                    <Text fontSize="xs" color={labelTextColor}>
                                                        by {template.createdBy}
                                                    </Text>
                                                )}
                                                {template.updatedAt && template.updatedAt !== template.createdAt && (
                                                    <Text fontSize="xs" color={labelTextColor}>
                                                        Updated: {formatDateAndTime(template.updatedAt).formattedDate}
                                                        {template.updatedBy && ` by ${template.updatedBy}`}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </Box>
                                    </Grid>
                                </Box>
                            </VStack>
                        </CardBody>
                    </Card>
                ))}

                {templates.length === 0 && (
                    <Card bg={cardBg} borderRadius="md" shadow="sm">
                        <CardBody p={8}>
                            <Flex justify="center" align="center" h="300px" direction="column">
                                <Icon as={FiMail} w={16} h={16} color="gray.400" mb={4} />
                                <Text color="gray.500" fontSize="lg" fontWeight="semibold" mb={2}>
                                    No email templates found
                                </Text>
                                <Text color="gray.400" fontSize="sm" textAlign="center" maxW="300px">
                                    Create your first template to get started with email automation
                                </Text>
                            </Flex>
                        </CardBody>
                    </Card>
                )}
            </Box>

            <EmailPreviewModal
                isOpen={isOpen}
                onClose={onClose}
                template={selectedTemplate}
            />
        </>
    );
};

export default TemplateTable;
